import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import {
  nextViewConnectBusinessState,
  previousViewConnectBusinessState
} from "../redux/features/connectBusinessStateSlice";
import { updateSignupEmployeeState } from "../redux/features/signupEmployeeStateSlice";
import { CheckEmployeeRequirements } from "./checkEmployeeRequirements";
import { connectBusinessFlowSx } from "./connectBusinessFlowStyles";
import { NextButton } from "./nextButton";

export const ConnectBusinessRequirements = ({
  checkedState,
  nextViewComponentState,
  previousViewEntityState,
  updateComponentState = updateSignupEmployeeState,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const selectedCount = Object.values(checkedState || {}).filter(Boolean).length;
  const buttonText = selectedCount > 0 ? "Next" : "Skip";

  const getWorkRequirements = async () => {
    try {
      setLoading(true);
      setLoadError("");
      const response = await gateway.get(`/workrequirements`);
      const data = response?.data?.data?.items ?? [];
      setRequirements(data);
    } catch (error) {
      console.error(error);
      setLoadError("We couldn’t load the requirements. Please try again.");
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWorkRequirements();
  }, []);

  return (
    <Box
      sx={{
        ...connectBusinessFlowSx.shell,
      }}
    >
      <Grid container sx={{ width: "100%", mx: "auto" }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              ...connectBusinessFlowSx.panel,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  ...connectBusinessFlowSx.iconWrap,
                }}
              >
                <AssignmentTurnedInIcon sx={{ fontSize: 28 }} />
              </Avatar>

              <Typography variant="h5" sx={connectBusinessFlowSx.title} gutterBottom>
                Submit Your Requirements
              </Typography>

              <Typography variant="body1" sx={connectBusinessFlowSx.copy}>
                Choose the requirements that apply to your business.
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
              >
                <Chip
                  icon={<CheckCircleOutlineIcon />}
                  label={`${selectedCount} selected`}
                  variant="outlined"
                  sx={{ borderColor: "var(--fx-surface-border, #E2E8F0)", borderRadius: "3px" }}
                />
              </Stack>
            </Box>

            {/* Content box */}
            <Box
              sx={{
                ...connectBusinessFlowSx.contentBand,
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 2, display: "block" }}
              >
                Select one or more requirements
              </Typography>

              {loading ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="rounded" height={44} />
                  <Skeleton variant="rounded" height={44} />
                  <Skeleton variant="rounded" height={44} />
                </Stack>
              ) : loadError ? (
                <Typography variant="body2" color="error">
                  {loadError}
                </Typography>
              ) : requirements.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No requirements available right now. You can skip and continue.
                </Typography>
              ) : (
                <CheckEmployeeRequirements
                  checkedState={checkedState}
                  data={requirements}
                  updateComponentState={updateComponentState}
                />
              )}
            </Box>

            {/* Footer: hint + Next/Back */}
            <Grid container alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {selectedCount > 0
                    ? "Great! Continue to see your best matches."
                    : "You can skip."}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ mt: { xs: 1.5, sm: 0 } }}>
                <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                  <NextButton
                    nextViewEntityState={nextViewConnectBusinessState}
                    previousViewEntityState={previousViewConnectBusinessState}
                    buttonText={buttonText}
                    sx={{
                      borderRadius: 32,
                      "& .MuiButton-root": {
                        borderRadius: 32,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
